'use client';

import { Card, Label } from '@/components/ui';
import { ScrollableContainer } from '../common/scrollable-container';
import { DistributionGraph } from '../common/distribution-graph';
import { StandardDeviationResult } from '../../../lib/stats/standard-deviation';

interface StandardDeviationCalculatorProps {
  visualizationData: StandardDeviationResult | null;
}

export function StandardDeviationCalculator({ visualizationData: result }: StandardDeviationCalculatorProps) {
  if (!result) {
    return null;
  }

  // Create labels for mean and ±1, ±2, ±3 standard deviations (population)
  const stdDevLabels = [
    { value: result.mean - 3 * result.population.standardDeviation, label: '-3σ', color: '#E57373' },
    { value: result.mean - 2 * result.population.standardDeviation, label: '-2σ', color: '#F06292' },
    { value: result.mean - result.population.standardDeviation, label: '-1σ', color: '#BA68C8' },
    { value: result.mean, label: 'Mean', color: '#4CAF50' },
    { value: result.mean + result.population.standardDeviation, label: '+1σ', color: '#BA68C8' },
    { value: result.mean + 2 * result.population.standardDeviation, label: '+2σ', color: '#F06292' },
    { value: result.mean + 3 * result.population.standardDeviation, label: '+3σ', color: '#E57373' }
  ];

  return (
    <ScrollableContainer>
      <div className="space-y-6">
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Population Statistics</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Standard Deviation (σ)</Label>
              <div className="text-lg font-semibold">
                {result.population.standardDeviation.toFixed(2)}
              </div>
            </div>
            <div>
              <Label>Variance (σ²)</Label>
              <div className="text-lg font-semibold">
                {result.population.variance.toFixed(2)}
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Sample Statistics</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Standard Deviation (s)</Label>
              <div className="text-lg font-semibold">
                {result.sample.standardDeviation.toFixed(2)}
              </div>
            </div>
            <div>
              <Label>Variance (s²)</Label>
              <div className="text-lg font-semibold">
                {result.sample.variance.toFixed(2)}
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Mean</h3>
          <div className="text-lg font-semibold">
            {result.mean.toFixed(2)}
          </div>
          <div className="mt-4">
            <Label>Interpretation</Label>
            <div className="text-sm text-muted-foreground">{result.interpretation}</div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Distribution Plot</h3>
          <DistributionGraph
            data={result.data}
            height={300}
            labels={stdDevLabels.map(({ value, label, color }) => ({
              value,
              text: label,
              color,
              lineStyle: label === 'Mean' ? 'solid' : 'dashed'
            }))}
            grid={{
              left: '15%',
              right: '5%',
              top: '10%',
              bottom: '15%'
            }}
          />
        </Card>
      </div>
    </ScrollableContainer>
  );
} 